import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactSketchCanvas } from "react-sketch-canvas";
import "../styles/propertydetails.css";
import Snackbar from "../components/snackbar";
import { useTranslation, Trans } from "react-i18next"; // 1. Import

const BACKEND_URL = "https://ddmwrl2uut5q8.cloudfront.net";

function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // 2. Initialize t and i18n
  const currentLanguage = i18n.language;

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("proposal");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesForModal, setImagesForModal] = useState([]);

  const sketchCanvasRef = useRef(null);
  const pdfInputRef = useRef(null);

  const [simpleFormData, setSimpleFormData] = useState({ name: "", company: "", email: "", phone: "" });
  const [designFormData, setDesignFormData] = useState({ name: "", company: "", email: "", phone: "", desiredArea: "", purpose: "", message: "", pdfRequest: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "info", position: "bottom-center", duration: 4000 });

  const showSnackbar = useCallback((message, type = "info", position = "bottom-center", duration = 4000) => {
    setSnackbar({ open: true, message, type, position, duration });
  }, []);

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/properties`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const foundProperty = data.find((item) => String(item.id) === id);
        if (foundProperty) {
          setProperty({
            ...foundProperty,
            image: foundProperty.image ? `${BACKEND_URL}${foundProperty.image}` : null,
            additionalImagesFullPaths: foundProperty.additionalImages?.map((img) => `${BACKEND_URL}${img}`) || [],
            thumb1: foundProperty.additionalImages?.[0] ? `${BACKEND_URL}${foundProperty.additionalImages[0]}` : null,
            thumb2: foundProperty.additionalImages?.[1] ? `${BACKEND_URL}${foundProperty.additionalImages[1]}` : null,
            thumb3: foundProperty.additionalImages?.[2] ? `${BACKEND_URL}${foundProperty.additionalImages[2]}` : null,
          });
        } else {
          console.warn(`Property with id ${id} not found.`);
          showSnackbar(t("propertyDetails.snackbar.notFound", { id }), "error", "top-center", 3000);
          setTimeout(() => navigate("/properties"), 3000);
        }
      })
      .catch((error) => {
        console.error("Error fetching property data:", error);
        showSnackbar(t("propertyDetails.snackbar.fetchError", { message: error.message }), "error", "top-center", 5000);
        setTimeout(() => navigate("/properties"), 5000);
      })
      .finally(() => setLoading(false));
  }, [id, navigate, showSnackbar, t]); // Added t to dependencies

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [currentView]);

  useEffect(() => {
    if (property) {
      const mainImage = property.image;
      const additional = property.additionalImagesFullPaths?.length > 0
        ? property.additionalImagesFullPaths
        : [property.thumb1, property.thumb2, property.thumb3].filter(Boolean);
      const allPropertyImages = [mainImage, ...additional].filter(imgUrl => imgUrl && typeof imgUrl === "string" && imgUrl.trim() !== "");
      setImagesForModal(allPropertyImages);
    } else {
      setImagesForModal([]);
    }
  }, [property]);

  const openModal = useCallback((index) => {
    if (imagesForModal.length > 0 && index >= 0 && index < imagesForModal.length) {
      setCurrentImageIndex(index); setIsModalOpen(true);
    }
  }, [imagesForModal]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const showNextImage = useCallback(() => {
    if (imagesForModal.length === 0) return;
    setCurrentImageIndex((prev) => (prev + 1) % imagesForModal.length);
  }, [imagesForModal.length]);
  const showPrevImage = useCallback(() => {
    if (imagesForModal.length === 0) return;
    setCurrentImageIndex((prev) => (prev - 1 + imagesForModal.length) % imagesForModal.length);
  }, [imagesForModal.length]);

  const handleGalleryImageClick = useCallback((imageSrc) => {
    if (!imageSrc || imagesForModal.length === 0) return;
    const index = imagesForModal.indexOf(imageSrc);
    if (index !== -1) openModal(index);
  }, [imagesForModal, openModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isModalOpen) {
        if (event.key === "Escape") closeModal();
        else if (event.key === "ArrowRight") showNextImage();
        else if (event.key === "ArrowLeft") showPrevImage();
      }
    };
    if (isModalOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, closeModal, showNextImage, showPrevImage]);

  const handleSimpleFormChange = (e) => setSimpleFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleDesignFormChange = (e) => {
    const { name, value, type, files } = e.target;
    setDesignFormData(prev => ({ ...prev, [name]: type === "file" ? files[0] : value }));
  };

  const handleSimpleSubmit = async (e) => {
    e.preventDefault(); if (isSubmitting) return; setIsSubmitting(true);
    showSnackbar(t("propertyDetails.snackbar.submittingSimple"), "info", "bottom-center", 0);
    try {
      const response = await fetch(`${BACKEND_URL}/simple`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...simpleFormData, propertyId: id }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showSnackbar(t("propertyDetails.snackbar.simpleSuccess"), "success", "top-right");
        setSimpleFormData({ name: "", company: "", email: "", phone: "" });
      } else {
        showSnackbar(t("propertyDetails.snackbar.submissionFailed", { message: result.message || t("propertyDetails.snackbar.unknownError") }), "error", "bottom-center", 6000);
      }
    } catch (error) {
      showSnackbar(t("propertyDetails.snackbar.submissionError", { message: error.message || t("propertyDetails.snackbar.tryAgain") }), "error", "bottom-center", 6000);
    } finally { setIsSubmitting(false); }
  };

  const handleDesignSubmit = async (e) => {
    e.preventDefault(); if (isSubmitting) return; setIsSubmitting(true);
    showSnackbar(t("propertyDetails.snackbar.submittingDesign"), "info", "bottom-center", 0);
    const formData = new FormData();
    Object.keys(designFormData).forEach(key => {
      if (key !== 'pdfRequest' && designFormData[key] !== null) formData.append(key, designFormData[key]);
    });
    formData.append("propertyId", id);
    if (designFormData.pdfRequest) formData.append("pdfRequest", designFormData.pdfRequest);

    if (sketchCanvasRef.current) {
      try {
        const drawingDataUrl = await sketchCanvasRef.current.exportImage("png");
        formData.append("drawingData", drawingDataUrl);
      } catch (error) {
        showSnackbar(t("propertyDetails.snackbar.drawingExportError"), "warning", "top-center", 5000);
      }
    }
    try {
      const response = await fetch(`${BACKEND_URL}/design`, { method: "POST", body: formData });
      const result = await response.json();
      if (response.ok && result.success) {
        showSnackbar(t("propertyDetails.snackbar.designSuccess"), "success", "top-right");
        setDesignFormData({ name: "", company: "", email: "", phone: "", desiredArea: "", purpose: "", message: "", pdfRequest: null });
        if (pdfInputRef.current) pdfInputRef.current.value = "";
        sketchCanvasRef.current?.clearCanvas();
      } else {
        showSnackbar(t("propertyDetails.snackbar.submissionFailed", { message: result.message || t("propertyDetails.snackbar.unknownError") }), "error", "bottom-center", 6000);
      }
    } catch (error) {
      showSnackbar(t("propertyDetails.snackbar.submissionError", { message: error.message || t("propertyDetails.snackbar.tryAgain") }), "error", "bottom-center", 6000);
    } finally { setIsSubmitting(false); }
  };

  if (loading) {
    return <div className="details-loading">{t("propertyDetails.loadingText")}</div>;
  }

  // --- Helper function to get translated string from property object ---
  const getTranslatedProp = (propField, fallbackKey) => {
    if (property && typeof property[propField] === 'object' && property[propField] !== null) {
      return property[propField][currentLanguage] || property[propField].en || property[propField].sq || t(fallbackKey) || "N/A";
    }
    return (property && property[propField]) || t(fallbackKey) || "N/A";
  };
  
  const propertyTitle = getTranslatedProp('title', 'propertyDetails.defaultTitle');
  const propertyPurpose = getTranslatedProp('purpose', 'propertyDetails.defaultPurpose');
  // For utilities, if it's an object of arrays:
  const propertyUtilitiesList = (property && property.utilities && typeof property.utilities === 'object')
    ? (property.utilities[currentLanguage] || property.utilities.en || property.utilities.sq || [])
    : (Array.isArray(property?.utilities) ? property.utilities : [t('propertyDetails.notAvailable')]); // Fallback if old array format or missing
  const propertyUtilities = propertyUtilitiesList.join(' | '); // Join if it's an array

  // For price_info and distance_info, assuming structured objects from backend
  const propertyPriceValue = property?.price_info?.value || t('propertyDetails.notAvailable');
  const propertyLeaseTerm = getTranslatedProp('price_info.lease_term', 'propertyDetails.notAvailable'); // if price_info.lease_term is {en, sq}
  const propertyPrice = property?.price || `${propertyPriceValue} (${propertyLeaseTerm})`; // Fallback to old property.price if price_info not present

  const propertyDistance = getTranslatedProp('distance_info', 'propertyDetails.notAvailable'); // if distance_info is {en, sq} directly
                                 // Or if distance_info is structured, you'd iterate and build it here.
                                 // Fallback to old property.distance
  const finalDistance = propertyDistance === t('propertyDetails.notAvailable') && property?.distance ? property.distance : propertyDistance;


  const propertyStatus = property?.status
    ? t(`propertyDetails.status.${property.status.toLowerCase().replace(/\s+/g, '_')}`, property.status.charAt(0).toUpperCase() + property.status.slice(1))
    : t('propertyDetails.status.unknown');


  return (
    <>
      <div className="property-details-page">
        {currentView === "proposal" && property && (
          <div className="property-section-wrapper relative overflow-hidden">
            <video className="absolute top-0 left-0 w-full h-full object-cover z-0" autoPlay loop muted playsInline>
              <source src="/background.mp4" type="video/mp4" />
              {t("propertyDetails.videoNotSupported")}
            </video>
            <div className="property-content relative z-10 bg-white/90 p-6 rounded-lg shadow-lg">
              <div className="property-images">
                <div className="main-image">
                  {property.image && (
                    <img src={property.image} alt={propertyTitle} onClick={() => handleGalleryImageClick(property.image)} loading="lazy" width="600" height="400" decoding="async" />
                  )}
                </div>
                <div className="side-thumbnails">
                  {(property.additionalImagesFullPaths?.slice(0, 3) || [property.thumb1, property.thumb2, property.thumb3].filter(Boolean))
                    .map((thumb, index) => thumb ? (
                      <img key={index} src={thumb} decoding="async" alt={t("propertyDetails.thumbnailAlt", { index: index + 1, title: propertyTitle })} onClick={() => handleGalleryImageClick(thumb)} loading="lazy" width="100" height="75" />
                    ) : null)}
                </div>
              </div>
           <div className="property-info">
  <div className="info-content">
    <h1>{propertyTitle}</h1>
    <p><strong>{t("propertyDetails.labels.area")}:</strong> {property?.area || t("propertyDetails.notAvailable")}</p>
    <p><strong>{t("propertyDetails.labels.purpose")}:</strong> {propertyPurpose}</p>
    <p><strong>{t("propertyDetails.labels.price")}:</strong> {propertyPrice}</p>
    <p><strong>{t("propertyDetails.labels.utilities")}:</strong> {propertyUtilities}</p>
    <p><strong>{t("propertyDetails.labels.distance")}:</strong> {finalDistance}</p>
    {property?.status && (
      <p><strong>{t("propertyDetails.labels.status")}:</strong> {propertyStatus}</p>
    )}
    {property?.objectId && (
      <p><strong>{t("propertyDetails.labels.propertyId")}:</strong> {property.objectId}</p>
    )}
    <div className="highlight-box">{t("propertyDetails.highlightBox")}</div>
  </div>

    <div className="property-buttons unified-buttons">
      <button className="btn-white" onClick={() => setCurrentView("simple_inquiry_new")} disabled={isSubmitting}>
        {t("propertyDetails.buttons.submitThisWay")}
      </button>

      <button onClick={() => setCurrentView("design_inquiry_new")} disabled={isSubmitting}>
        {t("propertyDetails.buttons.designInquiry")}
      </button>

      {property?.googleMapsUrl && (
        <button
          className="btn-gold"
          onClick={() => window.open(property.googleMapsUrl, "_blank", "noopener,noreferrer")}
        >
          {t("propertyDetails.buttons.viewOnMap")}
        </button>
      )}

      <button
        className="btn-gold"
        onClick={() => navigate("/video")}
      >
        {t("propertyDetails.buttons.viewOnVideo")}
      </button>
    </div>
</div>

            </div>
          </div>
        )}

        {!property && currentView === "proposal" && !loading && (
          <div className="details-loading">
            {t("propertyDetails.notFoundText")}
            <button onClick={() => navigate("/properties")}>{t("propertyDetails.buttons.backToProperties")}</button>
          </div>
        )}

        {currentView === "simple_inquiry_new" && (
          <div className="video-form-wrapper">
            <video className="form-background-video" autoPlay muted loop playsInline src="/background.mp4" />
            <div className="new-form-section-container simple-inquiry-variant">
              <button onClick={() => setCurrentView("proposal")} className="back-to-proposal-btn new-form-submit-button" disabled={isSubmitting}>
                {t("propertyDetails.buttons.backToProperty")}
              </button>
              <form className="new-form-layout collab-form" onSubmit={handleSimpleSubmit}>
                <h2>{t("propertyDetails.simpleForm.title")}</h2>
                <div className="new-form-row">
                  <input type="text" name="name" placeholder={t("propertyDetails.formPlaceholders.name")} required className="new-form-input-pill" value={simpleFormData.name} onChange={handleSimpleFormChange} disabled={isSubmitting} />
                  <input type="text" name="company" placeholder={t("propertyDetails.formPlaceholders.company")} className="new-form-input-pill" value={simpleFormData.company} onChange={handleSimpleFormChange} disabled={isSubmitting} />
                </div>
                <div className="new-form-row">
                  <input type="email" name="email" placeholder={t("propertyDetails.formPlaceholders.email")} required className="new-form-input-pill" value={simpleFormData.email} onChange={handleSimpleFormChange} disabled={isSubmitting} />
                  <input type="tel" name="phone" placeholder={t("propertyDetails.formPlaceholders.phone")} className="new-form-input-pill" value={simpleFormData.phone} onChange={handleSimpleFormChange} disabled={isSubmitting} />
                </div>
                <button type="submit" className="new-form-submit-button" disabled={isSubmitting}>
                  {isSubmitting ? t("propertyDetails.buttons.submitting") : t("propertyDetails.buttons.submitRequest")}
                </button>
              </form>
            </div>
          </div>
        )}

        {currentView === "design_inquiry_new" && (
          <div className="video-form-wrapper">
            <video className="form-background-video" autoPlay muted loop playsInline src="/background.mp4" />
            <div className="new-form-section-container design-inquiry-variant">
              <button onClick={() => setCurrentView("proposal")} className="back-to-proposal-btn new-form-submit-button" disabled={isSubmitting}>
                {t("propertyDetails.buttons.backToProperty")}
              </button>
              <form className="new-form-layout" onSubmit={handleDesignSubmit}>
                <h2>{t("propertyDetails.designForm.title")}</h2>
                <div className="grid">
                  <div className="collab-form input">
                    <input type="text" name="name" placeholder={t("propertyDetails.formPlaceholders.name")} required className="new-form-input-pill" value={designFormData.name} onChange={handleDesignFormChange} disabled={isSubmitting} />
                    <input type="text" name="company" placeholder={t("propertyDetails.formPlaceholders.company")} className="new-form-input-pill" value={designFormData.company} onChange={handleDesignFormChange} disabled={isSubmitting} />
                  </div>
                  <div className="collab-form input">
                    <input type="email" name="email" placeholder={t("propertyDetails.formPlaceholders.email")} required className="new-form-input-pill" value={designFormData.email} onChange={handleDesignFormChange} disabled={isSubmitting} />
                    <input type="tel" name="phone" placeholder={t("propertyDetails.formPlaceholders.phone")} className="new-form-input-pill" value={designFormData.phone} onChange={handleDesignFormChange} disabled={isSubmitting} />
                  </div>
                  <div className="collab-form input">
                    <input type="text" name="desiredArea" placeholder={t("propertyDetails.formPlaceholders.desiredArea")} className="new-form-input-pill" value={designFormData.desiredArea} onChange={handleDesignFormChange} disabled={isSubmitting} />
                    <div className="new-form-input-pill new-form-file-input-wrapper">
                      <span>{designFormData.pdfRequest ? designFormData.pdfRequest.name : t("propertyDetails.formPlaceholders.uploadPdf")}</span>
                      <input type="file" name="pdfRequest" accept=".pdf" onChange={handleDesignFormChange} ref={pdfInputRef} disabled={isSubmitting} />
                    </div>
                  </div>
                  <div className="collab-form input">
                    <input type="text" name="purpose" placeholder={t("propertyDetails.formPlaceholders.purpose")} className="new-form-input-pill" value={designFormData.purpose} onChange={handleDesignFormChange} disabled={isSubmitting} />
                    <input type="text" name="message" placeholder={t("propertyDetails.formPlaceholders.message")} className="new-form-input-pill" value={designFormData.message} onChange={handleDesignFormChange} disabled={isSubmitting} />
                  </div>
                </div>
                <div className="new-drawing-canvas-area">
                  <label className="new-drawing-canvas-label">{t("propertyDetails.designForm.sketchLabel")}</label>
                  <ReactSketchCanvas ref={sketchCanvasRef} strokeWidth={3} strokeColor="white" canvasColor="transparent" height="350px" width="100%" className="new-drawing-canvas-element" withTimestamp={false} />
                  <div className="new-drawing-canvas-controls">
                    <button type="button" onClick={() => sketchCanvasRef.current?.clearCanvas()} className="new-drawing-canvas-button" disabled={isSubmitting}>{t("propertyDetails.buttons.clear")}</button>
                    <button type="button" onClick={() => sketchCanvasRef.current?.undo()} className="new-drawing-canvas-button" disabled={isSubmitting}>{t("propertyDetails.buttons.undo")}</button>
                    <button type="button" onClick={() => sketchCanvasRef.current?.redo()} className="new-drawing-canvas-button" disabled={isSubmitting}>{t("propertyDetails.buttons.redo")}</button>
                  </div>
                </div>
                <button type="submit" className="new-form-submit-button" disabled={isSubmitting}>
                  {isSubmitting ? t("propertyDetails.buttons.submitting") : t("propertyDetails.buttons.submitRequest")}
                </button>
              </form>
            </div>
          </div>
        )}

        {isModalOpen && imagesForModal.length > 0 && (
          <div className="image-modal-overlay" onClick={closeModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={closeModal} aria-label={t("propertyDetails.modal.close")}>×</button>
              {imagesForModal.length > 1 && (<button className="modal-nav-btn modal-prev-btn" onClick={showPrevImage} aria-label={t("propertyDetails.modal.prev")}>❮</button>)}
              <img src={imagesForModal[currentImageIndex]} decoding="async" alt={t("propertyDetails.modal.imageAlt", { current: currentImageIndex + 1, total: imagesForModal.length, title: propertyTitle })} className="modal-image" loading="eager" />
              {imagesForModal.length > 1 && (<button className="modal-nav-btn modal-next-btn" onClick={showNextImage} aria-label={t("propertyDetails.modal.next")}>❯</button>)}
            </div>
          </div>
        )}
      </div>
      <Snackbar open={snackbar.open} message={snackbar.message} type={snackbar.type} position={snackbar.position} duration={snackbar.duration} onClose={handleCloseSnackbar} />
    </>
  );
}

export default PropertyDetails;